import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { fg } from '@atlaskit/platform-feature-flags';
import { Pressable as PlatformPressable } from '@atlaskit/primitives';
import { Pressable as NativePressable } from './native-pressable';
import { adaptEventHandler, useAdaptEventHandler } from '../../utils';
import { xcssValidator, type XCSSProp } from '../../utils/xcssValidator';
import { xcss as platformXCSS } from '@atlaskit/primitives';

type PlatformXCSSParam = Parameters<typeof platformXCSS>[0];
type PlatformPressableProps = React.ComponentProps<typeof PlatformPressable>;

export type PressableProps = Pick<PlatformPressableProps, 'children' | 'ref' | 'testId'> & {
	/**
	 * Token representing background color with a built-in fallback value.
	 */
	backgroundColor?: PlatformPressableProps['backgroundColor'];
	/**
	 * Whether the button is disabled.
	 */
	isDisabled?: PlatformPressableProps['isDisabled'];
	/**
	 * Handler called on click.
	 */
	onClick?: () => void;
	/**
	 * Tokens representing CSS shorthand for `paddingBlock` and `paddingInline` together.
	 *
	 * @see paddingBlock
	 * @see paddingInline
	 */
	padding?: PlatformPressableProps['padding'];
	/**
	 * Tokens representing CSS shorthand `paddingBlock`.
	 *
	 * @see paddingBlockStart
	 * @see paddingBlockEnd
	 */
	paddingBlock?: PlatformPressableProps['paddingBlock'];
	/**
	 * Tokens representing CSS `paddingBlockEnd`.
	 */
	paddingBlockEnd?: PlatformPressableProps['paddingBlockEnd'];
	/**
	 * Tokens representing CSS `paddingBlockStart`.
	 */
	paddingBlockStart?: PlatformPressableProps['paddingBlockStart'];
	/**
	 * Tokens representing CSS shorthand `paddingInline`.
	 *
	 * @see paddingInlineStart
	 * @see paddingInlineEnd
	 */
	paddingInline?: PlatformPressableProps['paddingInline'];
	/**
	 * Tokens representing CSS `paddingInlineEnd`.
	 */
	paddingInlineEnd?: PlatformPressableProps['paddingInlineEnd'];
	/**
	 * Tokens representing CSS `paddingInlineStart`.
	 */
	paddingInlineStart?: PlatformPressableProps['paddingInlineStart'];
	/**
	 * Apply a subset of permitted styles, powered by Atlassian Design System tokens.
	 * For a list of supported style properties on this component, see [here](/platform/forge/ui-kit/components/xcss).
	 *
	 * @type XCSSProp
	 */
	xcss?: XCSSProp;
};

const OldPressable = (props: Parameters<RenderFn>[0]) => {
	const {
		backgroundColor,
		isDisabled,
		onClick,
		padding,
		paddingBlock,
		paddingBlockEnd,
		paddingBlockStart,
		paddingInline,
		paddingInlineEnd,
		paddingInlineStart,
		testId,
		xcss,
	} = props.forgeDoc.props as PressableProps;
	return (
		<PlatformPressable
			backgroundColor={backgroundColor}
			children={props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
			isDisabled={isDisabled}
			onClick={adaptEventHandler(onClick)}
			padding={padding}
			paddingBlock={paddingBlock}
			paddingBlockEnd={paddingBlockEnd}
			paddingBlockStart={paddingBlockStart}
			paddingInline={paddingInline}
			paddingInlineEnd={paddingInlineEnd}
			paddingInlineStart={paddingInlineStart}
			testId={testId}
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			xcss={xcss && platformXCSS(xcssValidator(xcss) as PlatformXCSSParam)}
		/>
	);
};

const PressableWithAnalytics = (props: Parameters<RenderFn>[0]) => {
	const {
		backgroundColor,
		isDisabled,
		onClick,
		padding,
		paddingBlock,
		paddingBlockEnd,
		paddingBlockStart,
		paddingInline,
		paddingInlineEnd,
		paddingInlineStart,
		testId,
		xcss,
	} = props.forgeDoc.props as PressableProps;

	const adaptEventHandler = useAdaptEventHandler();

	return (
		<PlatformPressable
			backgroundColor={backgroundColor}
			children={props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
			isDisabled={isDisabled}
			onClick={adaptEventHandler(onClick)}
			padding={padding}
			paddingBlock={paddingBlock}
			paddingBlockEnd={paddingBlockEnd}
			paddingBlockStart={paddingBlockStart}
			paddingInline={paddingInline}
			paddingInlineEnd={paddingInlineEnd}
			paddingInlineStart={paddingInlineStart}
			testId={testId}
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			xcss={xcss && platformXCSS(xcssValidator(xcss) as PlatformXCSSParam)}
		/>
	);
};

/**
 * A pressable is a primitive for building custom buttons.
 */
export const Pressable = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	if (fg('platform_migrate_pressable_to_use_migrated_xcss')) {
		return <NativePressable {...props} />;
	}

	if (fg('forge-ui-performance-analytics-provider')) {
		return <PressableWithAnalytics {...props} />;
	}
	return <OldPressable {...props} />;
};
